/* eslint-disable @typescript-eslint/no-explicit-any */
import { createStore, reconcile } from "solid-js/store";

import { GetEncryptString } from "@/api/clientApi/Common";
import { useUrlStorage } from "@/hooks/useUrlStorage";
import { MinPlatCoin } from "@/lib/pointUtils.ts";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { updateWebPushPublicKey } from "@/lib/subscribe";
import { requestTwaData } from "@/lib/twaBridge";
import { dealString } from "@/lib/utils";
import { GamePlatListSelected } from "@/types/compontent/base/baseType";
import { CryptoConfItem } from "@/types/crypto";
import {
  AppCountryInfo,
  GetSysConfigRsp,
  GgamePlatform,
} from "@/types/swagger/front.ts";

import { setAndroidAppDownloadLinkConfig } from "./configStore";
import { initCryptoData } from "./cryptoStore";
import { deviceInfo } from "./deviceStore";

// WebView SDK 返回的数据结构类型定义
export interface WebViewReportData {
  cmd: string;
  facebook: {
    android_id: string;
    fb_deep_link_referer: string;
  };
  deeplink: string;
  playinstall: string;
  firebase: {
    android_id: string;
    firebase_app_instance_id: string;
    firebase_session_id: string;
    firebase_project_id: string;
    firebase_token: string;
  };
  appsflyer: {
    deepLinkModel: string;
    scheme: string;
    key: string;
    android_id: string;
    appsflyer_id: string;
    af_install_referer: string;
    af_deeplink_referer: string;
  };
  adjust: {
    pkg_name: string;
    ad_top_purchase_event: string;
    ad_login_event: string;
    ad_purchase_event: string;
    ad_logout_event: string;
    ad_app_token: string;
    ad_first_purchase_event: string;
    ad_register_event: string;
    ad_recharge_event: string;
    ad_first_open_event: string;
    android_id: string;
    ad_adid: string;
    ad_gps_adid: string;
    ad_install_referer: string;
    ad_deeplink_referer: string;
  };
  app_first_open: number;
}

export interface TwaReportData {
  adjust: {
    ad_app_token: string;
    android_id: string;
    ad_adid: string;
    ad_gps_adid: string;
    ad_install_referer: string;
    ad_deeplink_referer: string;
  };
  playinstall: {
    referrer_url: string;
  };
}

type SysConfigStore = {
  sysConfigData: GetSysConfigRsp;
  point_type: number;
  wallet_type: number;
  point_ratio: number;
  plat_coin_config: Record<string, unknown>;
  app_country_infos: AppCountryInfo[];
  game_plat_list: GgamePlatform[]; //游戏厂商列表【排序后】【合并了服务端的相同厂商】
  game_plat_list_selected: GamePlatListSelected[];
  gclass_game_list: any;
  initReady: boolean;
  platCoinPointType: number;
  crypto_maps: Record<number, CryptoConfItem>;
  currency_symbol: string;
  app_name: string;
  sport_config: Record<"sport_priority_type" | "sport_game_id", number>;
  // 与服务器时区偏差(秒)
  timeOffset: number;
  // 当前时间戳(秒)
  currentTimestamp: number;
  sdkData: WebViewReportData; //WebView SDK 返回的数据
  twaData: TwaReportData; //TWA SDK 返回的数据
  twaReady: boolean;
  serverHash: string; //TWA serverHash 服务端返回的hash
  deviceInfo: Record<string, unknown>; //TWA deviceInfo 设备信息
};

const [store, setConfigStore] = createStore<SysConfigStore>({
  sysConfigData: {} as GetSysConfigRsp,
  point_type: 5,
  wallet_type: 0,
  point_ratio: 0,
  plat_coin_config: {},
  app_country_infos: [],
  game_plat_list: [],
  gclass_game_list: {},
  initReady: false,
  game_plat_list_selected: [],
  platCoinPointType: 0,
  crypto_maps: {},
  currency_symbol: "R$",
  app_name: "",
  sport_config: {
    sport_priority_type: 0,
    sport_game_id: 0,
  },
  twaReady: false,
  timeOffset: 0,
  currentTimestamp: parseInt(`${Date.now() / 1000}`),
  sdkData: {} as WebViewReportData,
  twaData: {} as TwaReportData,
  serverHash: "",
  deviceInfo: {},
});
export const sysConfigStore = store;

export const initSysConfigStore = function (sysConfigData: GetSysConfigRsp) {
  setConfigStore("sysConfigData", reconcile(sysConfigData));
  setConfigStore("point_type", sysConfigData.point_type);
  setConfigStore("wallet_type", sysConfigData.wallet_type);
  setConfigStore("point_ratio", sysConfigData.point_ratio);
  setConfigStore("app_name", sysConfigData.app_name);
  setConfigStore("plat_coin_config", sysConfigData.plat_coin_config);
  setConfigStore("app_country_infos", sysConfigData.app_country_infos);
  setConfigStore(
    "sport_config",
    sysConfigData.sport_config || {
      sport_priority_type: 0,
      sport_game_id: 0,
    },
  );
  setConfigStore(
    "timeOffset",
    (-(new Date().getTimezoneOffset() / 60) - sysConfigData.time_zone) *
      60 *
      60,
  );

  setConfigStore(
    "game_plat_list",
    sysConfigData?.game_plat_list
      ?.filter((item) => item.status)
      .sort((a, b) => b.PlatId - a.PlatId),
  );

  // 去重, 避免出現同樣選項
  setConfigStore(
    "game_plat_list_selected",
    Array.from(
      new Map(
        store.game_plat_list?.map((item) => [
          item.tag,
          {
            label: item.name,
            value: item.tag,
            logo: item.logo,
          },
        ]),
      ).values(),
    ),
  );

  sysConfigData.crypto_currency_config?.forEach((item) => {
    setConfigStore("crypto_maps", item.point_type, item);
    if (item.point_type >= MinPlatCoin) {
      setConfigStore("platCoinPointType", item.point_type);
    }
  });
  initCryptoData(
    (sysConfigData.crypto_currency_config as CryptoConfItem[]) || [],
  );
  setConfigStore("initReady", true);
  updateWebPushPublicKey(sysConfigData.web_push_public_key);
  // app下载链接
  if (sysConfigData.android_app_download_link_config?.links?.length) {
    setAndroidAppDownloadLinkConfig(
      sysConfigData.android_app_download_link_config.links,
    );
  }
};

export const setServerHash = (serverHash: string) => {
  setConfigStore("serverHash", serverHash);
};

export const setDeviceInfo = (deviceInfo: Record<string, unknown>) => {
  setConfigStore("deviceInfo", deviceInfo);
};

export const tryGetServerHash = async () => {
  console.log("[twa] tryGetServerHash");
  const { getStoredParams } = useUrlStorage();
  const storedParams = getStoredParams();
  const android_id =
    storedParams?.params?.android_id || sysConfigStore.deviceInfo?.android_id;
  console.log("[twa] android_id", android_id);
  if (android_id) {
    const res = await GetEncryptString(android_id);
    console.log("[twa] android_id res", res);
    if (res && res.dest) {
      setConfigStore("serverHash", res.dest as string);
      requestTwaData("request_playintegrity_token", {
        reqHash: res.dest as string,
      });
    }
  }
};

export const setSdkData = (sdkData: WebViewReportData) => {
  setConfigStore("sdkData", reconcile(sdkData));
};

export const setTwaData = (twaData: TwaReportData) => {
  setConfigStore("twaData", reconcile(twaData));
};

export const setTwaReady = (twaReady: boolean) => {
  setConfigStore("twaReady", twaReady);
};

export const getSdkData = () => {
  const sdkData: any = sdkManager.getReportSdkData();
  console.log("getSdkData--publish--sdkData", sdkData);
  console.log("getSdkData--publish--sdkData--typeof", typeof sdkData);
  if (sdkData) {
    try {
      // iOS WebView 直接返回对象，Android 可能返回字符串
      const parsedData =
        typeof sdkData === "string" ? JSON.parse(sdkData) : sdkData;
      console.log("getSdkData--publish--parsedData", parsedData);
      setSdkData(parsedData);
    } catch (error) {
      console.error("getSdkData--publish----setSdkData--error", error);
    }
  }
};

export const getTwaData = () => {
  console.log("getTwaData--publish----request_adjust_attribution");
  requestTwaData("request_adjust_attribution");
  setTimeout(() => {
    console.log("getTwaData--publish----request_playinstall_referer");
    requestTwaData("request_playinstall_referrer");
  }, 100);
};

// 更新 TwaReportData 中的 adjust 字段
export const updateTwaAdjustData = (
  adjustData: Partial<TwaReportData["adjust"]>,
) => {
  setConfigStore(
    "twaData",
    reconcile({
      ...sysConfigStore.twaData,
      adjust: {
        ...sysConfigStore.twaData.adjust,
        ...adjustData,
      },
    }),
  );
};

// 更新 TwaReportData 中的 playinstall 字段
export const updateTwaPlayinstallData = (
  playinstallData: Partial<TwaReportData["playinstall"]>,
) => {
  setConfigStore(
    "twaData",
    reconcile({
      ...sysConfigStore.twaData,
      playinstall: {
        ...sysConfigStore.twaData.playinstall,
        ...playinstallData,
      },
    }),
  );
};

// 更新整个 TwaReportData
export const updateTwaData = (twaData: Partial<TwaReportData>) => {
  setConfigStore(
    "twaData",
    reconcile({
      ...sysConfigStore.twaData,
      ...twaData,
    }),
  );
};

export const getAppPackageInfoParams = () => {
  const adjust = sysConfigStore.sdkData?.adjust;
  const appPackageInfo: any = {};
  if (adjust?.ad_app_token) {
    appPackageInfo.package_name = adjust.pkg_name || "";
    appPackageInfo.sdks = [
      {
        token: adjust.ad_app_token,
        platform: 5,
      },
    ];
  }
  return appPackageInfo;
};
export const getAdReferer = () => {
  const ad_referer = {
    ad_install_referer:
      dealString(sysConfigStore.sdkData?.adjust?.ad_install_referer) || "",
    ad_deep_link_referer:
      dealString(sysConfigStore.sdkData?.adjust?.ad_deeplink_referer) || "",
    fb_deep_link_referer:
      dealString(sysConfigStore.sdkData?.facebook?.fb_deep_link_referer) || "",
    deep_link: dealString(sysConfigStore.sdkData?.deeplink) || "",
    play_install_referer: dealString(sysConfigStore.sdkData?.playinstall) || "",
  };

  if (deviceInfo.app_type == 14) {
    ad_referer.ad_install_referer =
      dealString(sysConfigStore.twaData?.adjust?.ad_install_referer) || "";
    ad_referer.ad_deep_link_referer =
      sysConfigStore.twaData?.adjust?.ad_deeplink_referer || "";
    ad_referer.fb_deep_link_referer = "";
    ad_referer.deep_link = dealString(sysConfigStore.sdkData?.deeplink) || "";

    const play_install_referer =
      sysConfigStore.twaData?.playinstall?.referrer_url || "";

    ad_referer.play_install_referer = play_install_referer;
  }
  return ad_referer;
};
export const getAdParams = () => {
  const { getStoredParams } = useUrlStorage();
  const getTwaAdParams = (): Record<string, string> => {
    return {
      android_id:
        getStoredParams()?.params?.android_id ||
        sysConfigStore.twaData?.adjust?.android_id ||
        "",
      ad_adid:
        getStoredParams()?.params?.ad_adid ||
        sysConfigStore.twaData?.adjust?.ad_adid ||
        "",
      ad_gps_adid:
        getStoredParams()?.params?.ad_gps_adid ||
        sysConfigStore.twaData?.adjust?.ad_gps_adid ||
        "",
      ad_app_token:
        getStoredParams()?.params?.ad_app_token ||
        sysConfigStore.twaData?.adjust?.ad_app_token ||
        "",
    };
  };

  const getSdkAdParams = (): Record<string, string> => {
    return {
      android_id:
        getStoredParams()?.params?.android_id ||
        sysConfigStore.sdkData?.adjust?.android_id ||
        "",
      ad_adid:
        getStoredParams()?.params?.ad_adid ||
        sysConfigStore.sdkData?.adjust?.ad_adid ||
        "",
      ad_gps_adid:
        getStoredParams()?.params?.ad_gps_adid ||
        sysConfigStore.sdkData?.adjust?.ad_gps_adid ||
        "",
      ad_app_token:
        getStoredParams()?.params?.ad_app_token ||
        sysConfigStore.sdkData?.adjust?.ad_app_token ||
        "",
      firebase_app_instance_id:
        sysConfigStore.sdkData?.firebase?.firebase_app_instance_id || "",
      firebase_token: sysConfigStore.sdkData?.firebase?.firebase_token || "",
      firebase_session_id:
        sysConfigStore.sdkData?.firebase?.firebase_session_id || "",
      firebase_project_id:
        sysConfigStore.sdkData?.firebase?.firebase_project_id || "",
    };
  };
  switch (deviceInfo.app_type) {
    case 14:
      return getTwaAdParams();
    default:
      return getSdkAdParams();
  }
};
